import {
  BackButton,
  Button,
  ErrorAlert,
  FormInput,
  PasswordInput,
  Text,
} from '@/components';
import PasswordRules from '@/components/molecules/Input/PasswordRules';
import EmailVerificationMessage from '@/components/organisms/Authentication/EmailVerificationMessage';
import { useAuth } from '@/components/providers/preauth/AuthProvider';
import {
  EMAIL_FORM_VALIDATIONS,
  PASSWORD_FORM_VALIDATIONS,
} from '@/utils/constants';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
interface Props {
  onBackClick: () => void;
  onSignInClick: () => void;
  onSignUpClick: (email: string, password: string) => void;
  onGoogleSignInClick?: () => void;
  error?: string;
  showVerification?: boolean;
}
export type SignUpFormFields = {
  email: string;
  password: string;
};
const SignUpForm: FC<Props> = ({
  onBackClick,
  onSignInClick,
  onSignUpClick,
  onGoogleSignInClick,
  error,
  showVerification = false,
}) => {
  const { isEmailVerified } = useAuth();
  const [userEmail, setUserEmail] = useState('');
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    watch,
  } = useForm<SignUpFormFields>({ criteriaMode: 'all' });
  useEffect(() => {
    if (error && !errors.email)
      setError('email', { type: 'custom', message: error });
  }, [error, errors.email, setError]);
  const handleOnSubmit = handleSubmit(
    ({ email, password }: SignUpFormFields) => {
      setUserEmail(email);
      onSignUpClick(email, password);
    },
  );
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.email) {
        setUserEmail(value.email);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);
  if (showVerification) {
    return (
      <EmailVerificationMessage
        email={userEmail}
        onBackToSignIn={onSignInClick}
        onBackClick={onBackClick}
      />
    );
  }
  return (
    <div className="flex flex-col gap-20 w-full">
      <div className="flex flex-col gap-md">
        <BackButton onBackClick={onBackClick} />
      </div>
      <form onSubmit={handleOnSubmit} className="flex flex-col gap-md">
        <div className="flex flex-col gap-10">
          <Text value="Sign up" size="h1" />
          <div className="flex flex-col gap-10">
            <FormInput
              name="email"
              label="Email"
              type="email"
              register={register}
              rules={{
                required: 'Enter email to continue',
                validate: EMAIL_FORM_VALIDATIONS,
              }}
              errors={errors}
              showError
            />
            <PasswordInput
              name="password"
              label="Password"
              type="password"
              register={register}
              rules={{
                required: '',
                validate: PASSWORD_FORM_VALIDATIONS,
              }}
              errors={errors}
              showError={false}
            />
            <PasswordRules value={watch('password')} errors={errors.password} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button type="submit" fullWidth>
            Sign up
          </Button>
          <div className="flex gap-2 items-baseline">
            <Text value="Already have an account?" color="tertiary" />
            <Text
              value="Sign in"
              intent="link"
              color="accent"
              onClick={onSignInClick}
            />
          </div>
        </div>
        {error && !errors.email && (
          <ErrorAlert title="Authentication Error" description={error} />
        )}
      </form>
    </div>
  );
};
export default SignUpForm;
